import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import AutoGrowTextarea from '../../components/AutoGrowTextarea/AutoGrowTextarea';
import PageMeta from '../../components/SEO/PageMeta';
import {
  getObjectToolPath,
  getObjectToolRoute,
  getObjectToolsByCategory,
  getObjectToolStructuredData,
  OBJECT_TOOL_CATEGORY_LABELS,
  ObjectToolCategory,
  ObjectToolSlug,
} from '../../config/objectTools';
import {
  diffObjects,
  flattenObject,
  formatJson,
  mergeObjects,
  minifyJson,
  objectToQueryString,
  parseObjectInput,
  sortObjectKeys,
} from '../../utils/developerToolTransforms';
import styles from '../StringToolsPage/StringToolsPage.module.css';

const SAMPLE_OBJECT = `{
  "user": {
    "id": 42,
    "name": "Ada",
    "roles": ["admin", "editor"]
  },
  "active": true
}`;

const SAMPLE_COMPARE_OBJECT = `{
  "user": {
    "id": 42,
    "name": "Ada Lovelace",
    "verified": true
  },
  "active": false
}`;

const TOOL_CATEGORIES: ObjectToolCategory[] = ['format', 'extract', 'transform', 'compare'];

export default function ObjectToolsPage() {
  const { toolSlug } = useParams<{ toolSlug: string }>();
  const route = getObjectToolRoute(toolSlug);
  const isMasterPage = !toolSlug;
  const [input, setInput] = useState(SAMPLE_OBJECT);
  const [compareInput, setCompareInput] = useState(SAMPLE_COMPARE_OBJECT);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const parsed = useMemo(() => parseObjectInput(input), [input]);
  const compareParsed = useMemo(() => parseObjectInput(compareInput), [compareInput]);
  const value = useMemo(() => parsed.value ?? {}, [parsed.value]);
  const compareValue = useMemo(() => compareParsed.value ?? {}, [compareParsed.value]);

  const results = useMemo(
    () => ({
      formatted: formatJson(value),
      minified: minifyJson(value),
      keys: Object.keys(value),
      values: Object.values(value),
      entries: Object.entries(value),
      sorted: sortObjectKeys(value),
      flattened: flattenObject(value),
      queryString: objectToQueryString(value),
      diff: diffObjects(value, compareValue),
      merged: mergeObjects(value, compareValue),
    }),
    [compareValue, value],
  );

  if (toolSlug && !route) {
    return <Navigate to={getObjectToolPath('json-formatter')} replace />;
  }

  const pageTitle = route?.title ?? 'Free Online JavaScript Object Tools';
  const pageDescription =
    route?.description ??
    'Free online JavaScript object tools for formatting, minifying, extracting keys and values, flattening, sorting keys, diffing, and merging objects.';
  const path = route ? getObjectToolPath(route.slug) : '/javascript-object-tools';
  const activeSlug = route?.slug;
  const isDedicatedComparePage = !isMasterPage && isCompareSlug(activeSlug);

  async function handleCopy(slug: string, output: string) {
    if (!output) return;

    try {
      await navigator.clipboard.writeText(output);
      setCopiedSlug(slug);
      window.setTimeout(() => setCopiedSlug(null), 1600);
    } catch {
      setCopiedSlug(null);
    }
  }

  function formatPrimaryInput() {
    if (parsed.value) {
      setInput(formatJson(parsed.value));
    }
  }

  function formatCompareInput() {
    if (compareParsed.value) {
      setCompareInput(formatJson(compareParsed.value));
    }
  }

  return (
    <>
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        path={path}
        structuredData={route ? getObjectToolStructuredData(route) : undefined}
      />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden> / </span>
        {route ? (
          <>
            <Link to="/javascript-object-tools">JavaScript Object Tools</Link>
            <span aria-hidden> / </span>
            <span>{route.shortLabel}</span>
          </>
        ) : (
          <span>JavaScript Object Tools</span>
        )}
      </nav>

      <section className={styles.hero}>
        <p className={styles.eyebrow}>Free online JavaScript object tools</p>
        <h1>{pageTitle}</h1>
        <p>{pageDescription}</p>
      </section>

      <nav className={styles.toolNavGroups} aria-label="Object tool shortcuts">
        {TOOL_CATEGORIES.map((category) => (
          <div key={category} className={styles.toolNavGroup}>
            <span className={styles.toolNavLabel}>{OBJECT_TOOL_CATEGORY_LABELS[category]}</span>
            <div className={styles.toolNav}>
              {getObjectToolsByCategory(category).map((tool) => (
                <Link
                  key={tool.slug}
                  to={getObjectToolPath(tool.slug)}
                  className={`${styles.toolLink} ${
                    tool.slug === activeSlug ? styles.activeToolLink : ''
                  }`}
                >
                  {tool.shortLabel}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {isDedicatedComparePage ? (
        <section className={styles.compareWorkspace} aria-labelledby="object-input-heading">
          <div className={styles.compareInputGrid}>
            <ObjectInputPanel
              id="object-input"
              label="Enter JavaScript object / JSON"
              input={input}
              rows={12}
              error={parsed.error}
              onChange={setInput}
              onFormat={formatPrimaryInput}
            />
            <ObjectInputPanel
              id="object-compare-input"
              label="Compare / merge object"
              input={compareInput}
              rows={12}
              error={compareParsed.error}
              onChange={setCompareInput}
              onFormat={formatCompareInput}
            />
          </div>
          <div className={styles.resultSections}>
            <ObjectCompareResults
              isMasterPage={false}
              activeSlug={activeSlug}
              routeLabel={route?.label}
              compareError={compareParsed.error}
              diffSummary={results.diff}
              diff={formatJson(results.diff)}
              merged={formatJson(results.merged)}
              copiedSlug={copiedSlug}
              onCopy={handleCopy}
            />
          </div>
        </section>
      ) : (
        <section
          className={`${styles.workspace} ${
            isMasterPage ? styles.masterWorkspace : styles.dedicatedWorkspace
          }`}
          aria-labelledby="object-input-heading"
        >
          <ObjectInputPanel
            id="object-input"
            label="Enter JavaScript object / JSON"
            input={input}
            rows={isMasterPage ? 7 : 14}
            error={parsed.error}
            helperText="Paste valid JSON object input for predictable results."
            onChange={setInput}
            onFormat={formatPrimaryInput}
          />

          <div className={styles.resultSections}>
            {(isMasterPage || isFormatSlug(activeSlug)) && (
              <ResultSection title={isMasterPage ? 'Format' : (route?.label ?? 'Format')}>
                {(isMasterPage || activeSlug === 'json-formatter') && (
                  <ResultCard
                    slug="json-formatter"
                    activeSlug={activeSlug}
                    title="JSON formatter"
                    value={results.formatted}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'json-minifier') && (
                  <ResultCard
                    slug="json-minifier"
                    activeSlug={activeSlug}
                    title="JSON minifier"
                    value={results.minified}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
              </ResultSection>
            )}

            {(isMasterPage || isExtractSlug(activeSlug)) && (
              <ResultSection title={isMasterPage ? 'Extract' : (route?.label ?? 'Extract')}>
                {(isMasterPage || activeSlug === 'object-keys') && (
                  <ResultCard
                    slug="object-keys"
                    activeSlug={activeSlug}
                    title="Object keys"
                    value={formatJson(results.keys)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'object-values') && (
                  <ResultCard
                    slug="object-values"
                    activeSlug={activeSlug}
                    title="Object values"
                    value={formatJson(results.values)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'object-entries') && (
                  <ResultCard
                    slug="object-entries"
                    activeSlug={activeSlug}
                    title="Object entries"
                    value={formatJson(results.entries)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
              </ResultSection>
            )}

            {(isMasterPage || isTransformSlug(activeSlug)) && (
              <ResultSection title={isMasterPage ? 'Transform' : (route?.label ?? 'Transform')}>
                {(isMasterPage || activeSlug === 'sort-object-keys') && (
                  <ResultCard
                    slug="sort-object-keys"
                    activeSlug={activeSlug}
                    title="Sort object keys"
                    value={formatJson(results.sorted)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'flatten-object') && (
                  <ResultCard
                    slug="flatten-object"
                    activeSlug={activeSlug}
                    title="Flatten object"
                    value={formatJson(results.flattened)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'object-to-query-string') && (
                  <ResultCard
                    slug="object-to-query-string"
                    activeSlug={activeSlug}
                    title="Object to query string"
                    value={results.queryString}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
              </ResultSection>
            )}

            {(isMasterPage || isCompareSlug(activeSlug)) && (
              <>
                {isMasterPage && (
                  <div className={styles.compareInputGrid}>
                    <ObjectInputPanel
                      id="object-compare-input"
                      label="Compare / merge object"
                      input={compareInput}
                      rows={5}
                      error={compareParsed.error}
                      onChange={setCompareInput}
                      onFormat={formatCompareInput}
                    />
                  </div>
                )}
                <ObjectCompareResults
                  isMasterPage={isMasterPage}
                  activeSlug={activeSlug}
                  routeLabel={route?.label}
                  compareError={compareParsed.error}
                  diffSummary={results.diff}
                  diff={formatJson(results.diff)}
                  merged={formatJson(results.merged)}
                  copiedSlug={copiedSlug}
                  onCopy={handleCopy}
                />
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
}

function ObjectInputPanel({
  id,
  label,
  input,
  rows,
  error,
  helperText,
  autoGrow = true,
  onChange,
  onFormat,
}: {
  id: string;
  label: string;
  input: string;
  rows: number;
  error: string | null;
  helperText?: string;
  autoGrow?: boolean;
  onChange: (value: string) => void;
  onFormat: () => void;
}) {
  return (
    <div className={styles.inputPanel}>
      <div className={styles.inputHeader}>
        <span>{label}</span>
        <button
          type="button"
          className={styles.copyButton}
          onClick={onFormat}
          disabled={Boolean(error)}
        >
          Format
        </button>
      </div>
      <label htmlFor={id} className={styles.srOnly}>
        {label}
      </label>
      <AutoGrowTextarea
        id={id}
        value={input}
        onValueChange={onChange}
        autoGrow={autoGrow}
        rows={rows}
        placeholder='{ "name": "Ada", "active": true }'
      />
      {helperText && <p className={styles.helperText}>{helperText}</p>}
      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
}

function ObjectCompareResults({
  isMasterPage,
  activeSlug,
  routeLabel,
  compareError,
  diffSummary,
  diff,
  merged,
  copiedSlug,
  onCopy,
}: {
  isMasterPage: boolean;
  activeSlug: ObjectToolSlug | undefined;
  routeLabel: string | undefined;
  compareError: string | null;
  diffSummary: {
    added: Record<string, unknown>;
    removed: Record<string, unknown>;
    changed: Record<string, { from: unknown; to: unknown }>;
  };
  diff: string;
  merged: string;
  copiedSlug: string | null;
  onCopy: (slug: string, value: string) => void;
}) {
  return (
    <ResultSection title={isMasterPage ? 'Compare' : (routeLabel ?? 'Compare')}>
      {compareError && <p className={styles.errorText}>{compareError}</p>}
      <ObjectDiffSummaryCard summary={diffSummary} />
      {(isMasterPage || activeSlug === 'object-diff') && (
        <ResultCard
          slug="object-diff"
          activeSlug={activeSlug}
          title="Object diff"
          value={diff}
          copiedSlug={copiedSlug}
          onCopy={onCopy}
        />
      )}
      {(isMasterPage || activeSlug === 'merge-objects') && (
        <ResultCard
          slug="merge-objects"
          activeSlug={activeSlug}
          title="Merge objects"
          value={merged}
          copiedSlug={copiedSlug}
          onCopy={onCopy}
        />
      )}
    </ResultSection>
  );
}

function ObjectDiffSummaryCard({
  summary,
}: {
  summary: {
    added: Record<string, unknown>;
    removed: Record<string, unknown>;
    changed: Record<string, { from: unknown; to: unknown }>;
  };
}) {
  return (
    <div className={styles.diffSummary}>
      <DiffGroup
        tone="removed"
        title="Removed"
        items={Object.entries(summary.removed).map(
          ([key, value]) => `${key}: ${formatJson(value)}`,
        )}
      />
      <DiffGroup
        tone="changed"
        title="Changed"
        items={Object.entries(summary.changed).map(
          ([key, value]) => `${key}: ${formatJson(value.from)} → ${formatJson(value.to)}`,
        )}
      />
      <DiffGroup
        tone="added"
        title="Added"
        items={Object.entries(summary.added).map(([key, value]) => `${key}: ${formatJson(value)}`)}
      />
    </div>
  );
}

function DiffGroup({
  tone,
  title,
  items,
}: {
  tone: 'removed' | 'changed' | 'added';
  title: string;
  items: string[];
}) {
  return (
    <section className={`${styles.diffGroup} ${styles[tone]}`}>
      <h3>
        {title} <span>{items.length}</span>
      </h3>
      {items.length > 0 ? (
        <ul>
          {items.map((item, index) => (
            <li key={`${item}-${index}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p>No values</p>
      )}
    </section>
  );
}

function ResultSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.resultSection}>
      <h2 className={styles.resultSectionTitle}>{title}</h2>
      <div className={styles.resultGrid}>{children}</div>
    </section>
  );
}

function ResultCard({
  slug,
  activeSlug,
  title,
  value,
  copiedSlug,
  onCopy,
}: {
  slug: ObjectToolSlug;
  activeSlug: ObjectToolSlug | undefined;
  title: string;
  value: string;
  copiedSlug: string | null;
  onCopy: (slug: string, value: string) => void;
}) {
  return (
    <article
      id={slug}
      className={`${styles.resultCard} ${slug === activeSlug ? styles.activeResultCard : ''}`}
    >
      <div className={styles.cardHeader}>
        <h3>{title}</h3>
        <button
          type="button"
          className={styles.copyButton}
          onClick={() => onCopy(slug, value)}
          disabled={!value}
        >
          {copiedSlug === slug ? 'Copied' : 'Copy'}
        </button>
      </div>
      <output>{value || '{}'}</output>
    </article>
  );
}

function isFormatSlug(slug: ObjectToolSlug | undefined): boolean {
  return slug === 'json-formatter' || slug === 'json-minifier';
}

function isExtractSlug(slug: ObjectToolSlug | undefined): boolean {
  return slug === 'object-keys' || slug === 'object-values' || slug === 'object-entries';
}

function isTransformSlug(slug: ObjectToolSlug | undefined): boolean {
  return (
    slug === 'sort-object-keys' || slug === 'flatten-object' || slug === 'object-to-query-string'
  );
}

function isCompareSlug(slug: ObjectToolSlug | undefined): boolean {
  return slug === 'object-diff' || slug === 'merge-objects';
}
